/**
 * Lecture du flux du canal de test (CH2) : décisions pures, testées.
 *
 * 90 % du temps le canal 2 est vide — image noire, silence. Ce n'est pas une
 * panne : les alertes « silence » et « image figée » ne valent que lorsqu'une
 * source ou un habillage y est envoyé. Une image noire figée est l'état normal
 * d'un canal vide, jamais une image figée.
 */

/** Silence prolongé avant alerte, quand quelque chose est envoyé. */
export const TEST_CHANNEL_SILENCE_ALERT_MS = 5_000
/** Image qui ne change plus avant alerte, quand quelque chose est envoyé. */
export const TEST_CHANNEL_FROZEN_ALERT_MS = 3_000
/** Crête sous laquelle on considère qu'il n'y a pas de son (dBFS). */
export const TEST_CHANNEL_SILENCE_DBFS = -60
/** Luminance moyenne (0–255) sous laquelle l'image est noire. */
export const TEST_CHANNEL_BLACK_LUMA = 16

export type TestChannelPlayerPhase = 'LOADING' | 'PLAYING' | 'UNAVAILABLE'

export type TestChannelDisplayState =
  | 'LOADING'
  | 'UNAVAILABLE'
  | 'EMPTY'
  | 'UNTRACKED'
  | 'FROZEN'
  | 'SILENCE'
  | 'MUTED'
  | 'PLAYING'

export interface TestChannelSignalInput {
  phase:        TestChannelPlayerPhase
  /** Une source ou un habillage est envoyé sur le canal 2 (état partagé). */
  occupied:     boolean
  muted:        boolean
  /** Durée depuis la dernière crête audible, en ms. */
  silentForMs:  number
  /** Durée depuis le dernier changement d'image, en ms. */
  frozenForMs:  number
  black:        boolean
}

/**
 * État affiché, du plus grave au plus calme. Le son coupé par l'opérateur
 * reste visible, mais une image figée prime : c'est elle qui ferait
 * tomber le test.
 */
export function testChannelDisplayState(input: TestChannelSignalInput): TestChannelDisplayState {
  if (input.phase === 'LOADING') return 'LOADING'
  if (input.phase === 'UNAVAILABLE') return 'UNAVAILABLE'
  // Rien d'envoyé depuis TrafficPlay : vide si l'image est noire. Une image
  // présente (envoyée hors TrafficPlay, par exemple à la main en AMCP) est
  // montrée telle quelle, sans alerte : on ne sait pas ce qu'elle doit être.
  if (!input.occupied) return input.black ? 'EMPTY' : 'UNTRACKED'
  if (input.frozenForMs >= TEST_CHANNEL_FROZEN_ALERT_MS && !input.black) return 'FROZEN'
  if (input.silentForMs >= TEST_CHANNEL_SILENCE_ALERT_MS) return 'SILENCE'
  if (input.muted) return 'MUTED'
  return 'PLAYING'
}

/** Crête d’un bloc d’échantillons (−1…1) convertie en dBFS, bornée à −90. */
export function samplesToDbfs(samples: ArrayLike<number>): number {
  if (!samples.length) return -90
  let peak = 0
  for (let index = 0; index < samples.length; index += 1) {
    const value = Math.abs(samples[index])
    if (value > peak) peak = value
  }
  if (peak <= 0) return -90
  return Math.max(-90, 20 * Math.log10(peak))
}

/** Position d'une crête (dBFS) sur la hauteur d'un vu-mètre gradué de −60 à 0. */
export function dbfsToMeterRatio(dbfs: number): number {
  if (!Number.isFinite(dbfs)) return 0
  return Math.min(1, Math.max(0, (dbfs + 60) / 60))
}

/**
 * Différence moyenne entre deux vignettes en niveaux de gris (même taille).
 * Sert à savoir si l'image bouge ; `null` si les tailles diffèrent.
 */
export function lumaDifference(previous: ArrayLike<number>, next: ArrayLike<number>): number | null {
  if (previous.length !== next.length || !next.length) return null
  let total = 0
  for (let index = 0; index < next.length; index += 1) total += Math.abs(next[index] - previous[index])
  return total / next.length
}

/** Luminance moyenne d'une vignette RGBA (ImageData.data). */
export function meanLumaRgba(rgba: ArrayLike<number>): number {
  const pixels = Math.floor(rgba.length / 4)
  if (!pixels) return 0
  let total = 0
  for (let pixel = 0; pixel < pixels; pixel += 1) {
    const offset = pixel * 4
    total += 0.2126 * rgba[offset] + 0.7152 * rgba[offset + 1] + 0.0722 * rgba[offset + 2]
  }
  return total / pixels
}

/** Débit lisible : « 9,6 Mb/s », « 420 kb/s ». */
export function formatBitrate(bitsPerSecond: number | null): string | null {
  if (bitsPerSecond === null || !Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return null
  if (bitsPerSecond >= 1_000_000) return `${(bitsPerSecond / 1_000_000).toFixed(1).replace('.', ',')} Mb/s`
  return `${Math.round(bitsPerSecond / 1000)} kb/s`
}
